import { format } from "date-fns";
import { Calendar, Clock, MapPin, Package, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SpecialHandlingIndicator } from "@/components/SpecialHandlingIndicator";
import { formatRetailerName } from "@/lib/formatRetailer";
import type { UserPickup } from "@/types/database";

interface PackageInfo {
  id: string;
  retailer: string;
  size: string;
}

interface PickupRequestCardProps {
  pickup: UserPickup;
  packages: PackageInfo[];
  remainingPickups: number;
  maxPickups: number;
  onApprove: (pickupId: string) => void;
  onDeny: (pickupId: string) => void;
  loading?: boolean;
}

export const PickupRequestCard = ({
  pickup,
  packages,
  remainingPickups,
  maxPickups,
  onApprove,
  onDeny,
  loading = false,
}: PickupRequestCardProps) => {
  return (
    <Card className="border-primary/20 shadow-medium">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Pickup Proposal
          </CardTitle>
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
            Awaiting Your Confirmation
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Proposed Date & Time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <Calendar className="h-5 w-5 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Proposed Date</p>
              <p className="font-medium">
                {format(new Date(pickup.proposed_date), "EEEE, MMMM d, yyyy")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <Clock className="h-5 w-5 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Time Window</p>
              <p className="font-medium">{pickup.proposed_time_slot}</p>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
          <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-muted-foreground">Pickup Address</p>
            <p className="font-medium">{pickup.address}</p>
          </div>
        </div>

        {/* Packages Included */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-medium">
              {packages.length} Package{packages.length !== 1 ? "s" : ""} Included
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {packages.map((pkg) => (
              <Badge key={pkg.id} variant="secondary" className="text-xs flex items-center gap-1 w-fit">
                {formatRetailerName(pkg.retailer)} - {pkg.size}
                <SpecialHandlingIndicator size={pkg.size} className="w-3 h-3 text-purple-500 fill-purple-500" />
              </Badge>
            ))}
          </div>
        </div>

        {/* Subscription Info */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/10">
          <span className="text-sm text-muted-foreground">Remaining pickups this month</span>
          <Badge variant="outline" className="font-semibold">
            {remainingPickups} of {maxPickups}
          </Badge>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            onClick={() => onApprove(pickup.id)}
            disabled={loading}
            className="flex-1 min-h-[44px]"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Approve Pickup
          </Button>
          <Button
            variant="outline"
            onClick={() => onDeny(pickup.id)}
            disabled={loading}
            className="flex-1 min-h-[44px]"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Request Change
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
